"use client";

import { useCallback, useEffect, useState, useRef } from "react";
import { FeaturedSlide } from "./featured-slide";
import { CarouselThumbnails } from "./carousel-thumbnails";
import { getFeaturedProductsForCarousel, type FeaturedProduct } from "@/lib/services/productService";
import { Skeleton } from "@/components/ui/skeleton";

export function FeaturedCarousel() {
  // Data fetching state
  const [products, setProducts] = useState<FeaturedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(-1); // -1 means show default featuredImageUrl
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  const totalSlides = products.length;

  // Fetch featured products on mount
  useEffect(() => {
    async function loadFeaturedProducts() {
      try {
        setIsLoading(true);
        setError(null);
        const result = await getFeaturedProductsForCarousel();

        if (result.success && result.data) {
          setProducts(result.data);
        } else {
          setError(result.error?.message || "Failed to load featured products");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load featured products");
      } finally {
        setIsLoading(false);
      }
    }

    loadFeaturedProducts();
  }, []);

  // Navigation functions
  const scrollPrev = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
    setSelectedScreenshotIndex(-1); // Reset to default image
    setTimeout(() => setIsTransitioning(false), 100);
  }, [isTransitioning, totalSlides]);

  const scrollNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    setSelectedScreenshotIndex(-1); // Reset to default image
    setTimeout(() => setIsTransitioning(false), 100);
  }, [isTransitioning, totalSlides]);

  const scrollTo = useCallback(
    (index: number) => {
      if (isTransitioning || index === currentIndex) return;
      setIsTransitioning(true);
      setCurrentIndex(index);
      setSelectedScreenshotIndex(-1); // Reset to default image
      setTimeout(() => setIsTransitioning(false), 100);
    },
    [isTransitioning, currentIndex]
  );

  // Autoplay
  useEffect(() => {
    if (isPaused) return;

    autoplayRef.current = setInterval(() => {
      scrollNext();
    }, 5000);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [scrollNext, isPaused]);

  // Pause autoplay on mouse enter
  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        scrollPrev();
      } else if (e.key === "ArrowRight") {
        scrollNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scrollPrev, scrollNext]);

  // Screenshot navigation handlers
  const handleThumbnailHover = useCallback((index: number) => {
    setSelectedScreenshotIndex(index);
  }, []);

  const handleThumbnailLeave = useCallback(() => {
    setSelectedScreenshotIndex(-1); // Reset to default featuredImageUrl
  }, []);

  const currentProduct = products[currentIndex];

  // Loading state - skeleton
  if (isLoading) {
    return (
      <section className="bg-background border-b border-border">
        <div className="container max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-6">
            <Skeleton className="h-4 w-36" />
            <div className="flex items-center gap-3">
              <Skeleton className="h-4 w-10" />
              <div className="flex items-center gap-2">
                <Skeleton className="w-8 h-8 rounded-lg" />
                <Skeleton className="w-8 h-8 rounded-lg" />
              </div>
            </div>
          </div>

          {/* Bento grid skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main featured card skeleton */}
            <div className="lg:col-span-9">
              <div className="h-[450px] md:h-[550px] lg:h-[600px] overflow-hidden rounded-2xl bg-card border border-border relative">
                <Skeleton className="absolute inset-0 rounded-2xl" />
                {/* Content overlay skeleton */}
                <div className="absolute bottom-0 left-0 right-0 p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-14 h-14 rounded-xl" />
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-48" />
                      <Skeleton className="h-4 w-64" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-20 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                </div>
              </div>
            </div>

            {/* Thumbnails strip skeleton */}
            <div className="lg:col-span-3 h-[600px]">
              <div className="flex flex-col gap-3 h-full">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="flex-1 rounded-xl" />
                ))}
              </div>
            </div>
          </div>

          {/* Navigation dots skeleton */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <Skeleton className="h-1.5 w-6 rounded-full" />
            <Skeleton className="h-1.5 w-1.5 rounded-full" />
            <Skeleton className="h-1.5 w-1.5 rounded-full" />
            <Skeleton className="h-1.5 w-1.5 rounded-full" />
          </div>
        </div>
      </section>
    );
  }

  // Error or empty state - don't show the carousel
  if (error || products.length === 0) {
    return null;
  }

  return (
    <section className="bg-background border-b border-border">
      <div className="container max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Simple header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-sm font-medium text-foreground-secondary">
            Избранное недели
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-foreground-tertiary">
              {currentIndex + 1} / {totalSlides}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={scrollPrev}
                disabled={isTransitioning}
                className="w-8 h-8 flex items-center justify-center border border-border rounded-lg disabled:opacity-40"
                aria-label="Предыдущий продукт"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                onClick={scrollNext}
                disabled={isTransitioning}
                className="w-8 h-8 flex items-center justify-center border border-border rounded-lg disabled:opacity-40"
                aria-label="Следующий продукт"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Bento grid layout - Image-first design */}
        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-6"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Main featured card - 9 cols (larger for image focus) */}
          <div className="lg:col-span-9">
            <div className="h-[450px] md:h-[550px] lg:h-[600px] overflow-hidden rounded-2xl bg-card border border-border">
              <div key={currentIndex} className="h-full w-full">
                <FeaturedSlide
                  product={currentProduct}
                  selectedScreenshotIndex={selectedScreenshotIndex}
                />
              </div>
            </div>
          </div>

          {/* Thumbnails strip - 3 cols (smaller) */}
          <div className="lg:col-span-3 h-[600px]">
            <CarouselThumbnails
              thumbnails={currentProduct.previewImages.slice(1)}
              selectedIndex={selectedScreenshotIndex}
              onThumbnailHover={handleThumbnailHover}
              onThumbnailLeave={handleThumbnailLeave}
            />
          </div>
        </div>

        {/* Navigation dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              disabled={isTransitioning}
              className={`h-1.5 rounded-full ${
                index === currentIndex
                  ? "w-6 bg-foreground"
                  : "w-1.5 bg-border"
              }`}
              aria-label={`Перейти к слайду ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
